import { InteractionRequiredAuthError } from '@azure/msal-browser'

export const LOGIN_SCOPES = ['openid', 'profile', 'email']

export function assertMicrosoftAuthSupported() {
  if (window.isSecureContext && window.crypto?.subtle) {
    return
  }

  throw new Error(
    `Microsoft OAuth requires HTTPS or localhost in the browser. The current frontend origin is ${window.location.origin}, which is not a secure context for MSAL.`
  )
}

export function createMsalConfig(runtimeConfig) {
  const origin = window.location.origin
  return {
    auth: {
      clientId: runtimeConfig.msClientId,
      authority: `https://login.microsoftonline.com/${runtimeConfig.msTenantId}`,
      redirectUri: origin,
      postLogoutRedirectUri: origin,
    },
    cache: {
      cacheLocation: 'sessionStorage',
    },
  }
}

export function getActiveAccount(instance, accounts = []) {
  return instance.getActiveAccount() || accounts[0] || null
}

export function getUserEmail(account) {
  const claims = account?.idTokenClaims || {}
  return claims.preferred_username || claims.email || claims.upn || account?.username || ''
}

export function buildUserProfile(account) {
  if (!account) {
    return null
  }
  return {
    email: getUserEmail(account),
    name: account.name || getUserEmail(account),
    username: getUserEmail(account),
  }
}

export async function acquireMicrosoftIdToken(instance, account) {
  if (!account) {
    throw new Error('No Microsoft account is signed in.')
  }

  try {
    const response = await instance.acquireTokenSilent({
      account,
      scopes: LOGIN_SCOPES,
    })
    return response.idToken
  } catch (error) {
    const isInteractionRequired =
      error instanceof InteractionRequiredAuthError ||
      error?.errorCode === 'interaction_required' ||
      error?.errorCode === 'consent_required' ||
      error?.errorCode === 'login_required'
    if (isInteractionRequired) {
      await instance.acquireTokenRedirect({
        account,
        scopes: LOGIN_SCOPES,
      })
      throw new Error('Redirecting to Microsoft sign-in.')
    }
    throw error
  }
}

export async function fetchWithMicrosoftAuth(instance, account, input, init = {}) {
  const token = await acquireMicrosoftIdToken(instance, account)
  const headers = new Headers(init.headers || {})
  headers.set('Authorization', `Bearer ${token}`)
  return fetch(input, {
    ...init,
    headers,
  })
}
